/**
 * A context-based hook that allows components to determine the view of the
 * "IOLDetails" page. The API is the same as useContext.
 */

import React, { useState, useCallback, useContext, createContext } from "react"
import PropTypes from "prop-types"
import useLocalStorage from "./use-local-storage"
import { defaultIOLDetailsTableColumns } from "../data/iol-details-table-columns"

const IOLDetailsViewPreferencesContext = createContext()

export const IOLDetailsViewPreferencesProvider = props => {
  const defaultSavedPreferencesConfig = new Map([
    ["stripes", true],
    ["columns", defaultIOLDetailsTableColumns],
  ]);
  // Get shipment view preferences out of local storage
  const [savedPreferences, persistPreferences] = useLocalStorage(
    "iol-details-view-preferences"
  )

  // Initialize state value falling back to the default values defined above
  const [preferences, setPreferences] = useState(
    // Revive preference map from local storage if it exists
    savedPreferences
      // add new columns to local storage if the user has outdated column structure
      ? new Map([
        ["stripes", new Map(savedPreferences).get("stripes")],
        ["columns", { ...defaultSavedPreferencesConfig.get("columns"), ...new Map(savedPreferences).get("columns") }],
      ])
      : defaultSavedPreferencesConfig
  )

  // When the user changes the preferences, persist the value to local storage
  // and update the state value
  const changePreferences = useCallback(
    values => {
      setPreferences(values)
      // Convert the values Map to an array when putting into local storage
      // so it can be serialized with JSON.stringify
      persistPreferences(Array.from(values.entries()))
    },
    [persistPreferences]
  )

  return (
    <IOLDetailsViewPreferencesContext.Provider
      value={[preferences, changePreferences]}
    >
      {props.children}
    </IOLDetailsViewPreferencesContext.Provider>
  )
}

IOLDetailsViewPreferencesProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

const useIOLDetailsViewPreferences = () =>
  useContext(IOLDetailsViewPreferencesContext)

export default useIOLDetailsViewPreferences